import { WorkawareApi } from 'apis/WorkawareApi'
import LoadingIndicator from 'components/LoadingIndicator'
import PdfTron from 'components/pdf/PdfTron'
import { createNotification } from 'factories/notificationFactory'
import { User } from 'oidc-client'
import React from 'react'
import { Helmet } from 'react-helmet'
import { useIntl } from 'react-intl'
import { getUrlVars } from 'utils/utilities'

interface Props {
  user?: User
}

const EditPdf = ({ user }: Props) => {
  const vars = React.useMemo(() => getUrlVars({ tid: '', id: '' }), [])
  const api = React.useMemo(() => new WorkawareApi(), [])
  const intl = useIntl()
  const [blob, setBlob] = React.useState<Blob | undefined>(undefined)
  const [working, setWorking] = React.useState(false)
  const [formId, setFormId] = React.useState<number | undefined>(undefined)
  const [templateId, setTemplateId] = React.useState<number | undefined>(undefined)
  const fetchStarted = React.useRef(false)

  React.useEffect(() => {
    if (!user) return

    const tidNum = vars.tid !== '' ? parseInt(vars.tid) : undefined
    const idNum = vars.id !== '' ? parseInt(vars.id) : undefined

    if (idNum !== undefined && formId === undefined) setFormId(idNum)
    if (tidNum !== undefined && templateId === undefined) setTemplateId(tidNum)

    if (blob !== undefined || fetchStarted.current) return

    const resolvedFormId = idNum ?? formId
    const resolvedTemplateId = tidNum ?? templateId

    if (resolvedFormId !== undefined) {
      fetchStarted.current = true
      api.forms.detail.get(resolvedFormId)
        .then((b) => setBlob(b))
        .catch(() => {
          fetchStarted.current = false
          createNotification.error(intl.formatMessage({ id: 'editPdf.loadError', defaultMessage: 'Failed to load the PDF' }))
        })
    } else if (resolvedTemplateId !== undefined) {
      fetchStarted.current = true
      api.forms.templates.getBlank(resolvedTemplateId)
        .then((b) => setBlob(b))
        .catch(() => {
          fetchStarted.current = false
          createNotification.error(intl.formatMessage({ id: 'editPdf.loadError', defaultMessage: 'Failed to load the PDF' }))
        })
    }
  // eslint-disable-next-line
  }, [user, formId, templateId])

  const handleSave = async (blob) => {
    setWorking(true)
    if (formId === undefined && templateId !== undefined) {
      let completion = await api.forms.templates.createFormCompletionRecord(templateId)
      setFormId(completion.Id)
      if (completion.Id === undefined) {
        setWorking(false)
        createNotification.error(intl.formatMessage({ id: 'document-saved-error', defaultMessage: 'Could not find the form ID' }))
        return
      }
      var bytes = await blob.arrayBuffer()
      api.forms.detail
        .post(completion.Id, bytes)
        .then(() => {
          setWorking(false)
          createNotification.success(intl.formatMessage({ id: 'document-saved-success', defaultMessage: 'Document Saved Successfully' }))
        })
        .finally(() => setWorking(false))
    } else if (formId !== undefined && templateId === undefined) {
      var bytes = await blob.arrayBuffer()
      api.forms.detail
        .post(formId, bytes)
        .then(() => {
          setWorking(false)
          createNotification.success(intl.formatMessage({ id: 'document-saved-success', defaultMessage: 'Document Saved Successfully' }))
        })
        .finally(() => setWorking(false))
    }
  }

  return (
    <>
      <Helmet>
        <title>Workaware: PDF</title>
      </Helmet>
      {!blob && <LoadingIndicator />}
      {blob && <PdfTron handleSave={(blob) => handleSave(blob)} blob={blob} working={working} {...vars} />}
    </>
  )
}

export default EditPdf
